/**
 * Photos and video an agent sends, through ant's chat media bucket.
 *
 * The file never passes through ant: ant hands out a signed PUT for one
 * object in the conversation (requestChatMediaUpload), the browser PUTs the
 * bytes straight to the bucket with the headers ant signed into the URL
 * (content type and size range, so the bucket itself refuses another file),
 * and the message then names the object (ChatMessageAttachmentInput). ant
 * checks the object with a HEAD of its own before it stores the message.
 *
 * The bucket must allow this origin (CORS) for PUT with the `Content-Type`,
 * `x-goog-content-length-range` and `x-goog-if-generation-match` headers.
 */

import {
  ChatMediaUploadSlot,
  ChatMessageAttachmentInput,
  requestChatMediaUpload,
} from './api';

/** The agent (or leaving the conversation) stopped the upload: not a failure. */
export class MediaUploadCanceled extends Error {
  constructor() {
    super('The upload was canceled');
    this.name = 'MediaUploadCanceled';
  }
}

/** ant's poster limits: a JPEG of at most 1 MB. */
const POSTER_TYPE = 'image/jpeg';
const MAX_POSTER_BYTES = 1024 * 1024;

/** GCS answers a refused request with `<Error><Code>...</Code>...</Error>`. */
function refusal(status: number, body: string): string {
  const code = body.match(/<Code>([^<]+)<\/Code>/)?.[1];
  return `The media storage refused the upload (${
    code ? `${code}, ` : ''
  }HTTP ${status}).`;
}

/**
 * PUTs `body` to the slot's signed URL with every header the slot gave,
 * verbatim. `onProgress` gets the bytes sent so far. Rejects with
 * MediaUploadCanceled when `signal` aborts, and with the bucket's reason
 * otherwise.
 *
 * ant signs the PUT with `x-goog-if-generation-match: 0` (the key must be
 * empty), so a 412 means an earlier try of this same PUT landed although
 * its answer was lost: the object is there, and that counts as uploaded.
 */
export function putToBucket(
  slot: ChatMediaUploadSlot,
  body: Blob,
  options: { onProgress?: (loadedBytes: number) => void; signal?: AbortSignal } = {}
): Promise<void> {
  const { onProgress, signal } = options;
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new MediaUploadCanceled());
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const settle = (done: () => void) => {
      signal?.removeEventListener('abort', onAbort);
      done();
    };

    xhr.open('PUT', slot.uploadUrl);
    slot.headers.forEach(({ name, value }) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded);
    };
    xhr.onload = () =>
      settle(() => {
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 412) {
          onProgress?.(body.size);
          resolve();
        } else {
          reject(new Error(refusal(xhr.status, xhr.responseText || '')));
        }
      });
    xhr.onerror = () =>
      settle(() =>
        reject(
          new Error(
            'Could not reach the media storage. Check the connection and try again.'
          )
        )
      );
    xhr.onabort = () => settle(() => reject(new MediaUploadCanceled()));
    signal?.addEventListener('abort', onAbort);
    xhr.send(body);
  });
}

export interface MediaUpload {
  /**
   * ant's SESSION id (ChatSession.id): an admin's slot is for the open
   * session the file will be sent into.
   */
  sessionId: string;
  file: File;
  kind: 'photo' | 'video';
  /** A video's length, as the browser read it. */
  durationMs?: number | null;
  width?: number | null;
  height?: number | null;
  /**
   * A video's still frame. Left out of the message when it is not a JPEG of
   * at most 1 MB, or cannot be uploaded: a video without a poster still plays.
   */
  poster?: Blob | null;
  /** From 0 to 1, over the file and its poster. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

const throwIfCanceled = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new MediaUploadCanceled();
};

/**
 * Uploads a photo or video (and a video's poster) into the session and
 * returns the attachment the message carries. Rejects with ant's or the
 * bucket's reason, or MediaUploadCanceled.
 */
export async function uploadChatMedia(
  upload: MediaUpload
): Promise<ChatMessageAttachmentInput> {
  const { sessionId, file, kind, signal, onProgress } = upload;
  const poster =
    kind === 'video' &&
    upload.poster &&
    upload.poster.type === POSTER_TYPE &&
    upload.poster.size <= MAX_POSTER_BYTES
      ? upload.poster
      : null;
  const total = file.size + (poster?.size ?? 0);
  const report = (loaded: number) =>
    onProgress?.(total > 0 ? Math.min(loaded / total, 1) : 1);

  report(0);
  const slot = await requestChatMediaUpload({
    sessionId,
    kind,
    contentType: file.type,
    byteSize: file.size,
  });
  throwIfCanceled(signal);
  await putToBucket(slot, file, { signal, onProgress: report });

  const attachment: ChatMessageAttachmentInput = {
    objectKey: slot.objectKey,
    kind,
    contentType: file.type,
    byteSize: file.size,
    ...(kind === 'video' && upload.durationMs ? { durationMs: upload.durationMs } : {}),
    ...(upload.width ? { width: upload.width } : {}),
    ...(upload.height ? { height: upload.height } : {}),
  };

  if (poster) {
    try {
      throwIfCanceled(signal);
      const posterSlot = await requestChatMediaUpload({
        sessionId,
        kind: 'poster',
        contentType: poster.type,
        byteSize: poster.size,
      });
      throwIfCanceled(signal);
      await putToBucket(posterSlot, poster, {
        signal,
        onProgress: (loaded) => report(file.size + loaded),
      });
      attachment.posterKey = posterSlot.objectKey;
    } catch (error) {
      if (error instanceof MediaUploadCanceled) throw error;
      // The poster is a nicety: the video goes without one.
    }
  }

  // The cancel may land as the last PUT settles, after its abort listener
  // is off: what the agent removed must not reach the message.
  throwIfCanceled(signal);
  report(total);
  return attachment;
}
